import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Upload, LogOut } from 'lucide-react';
import { fetchResources, fetchResourceTypes, getAdminToken, adminLogout } from '../../api/client';

export default function AdminToolsList() {
  const [tools, setTools] = useState([]);
  const [types, setTypes] = useState([]);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (!getAdminToken()) {
      nav('/admin/login');
      return;
    }
    fetchResourceTypes().then(setTypes).catch(() => setTypes([]));
    load();
  }, [nav]);

  const load = async (query = q, type = typeFilter) => {
    setLoading(true);
    const params = { limit: 200, sort: 'name' };
    if (query) params.q = query;
    if (type) params.type = type;
    const data = await fetchResources(params);
    setTools(data);
    setLoading(false);
  };

  const onSearch = (e) => {
    e.preventDefault();
    load(q, typeFilter);
  };

  const onLogout = async () => {
    await adminLogout();
    nav('/admin/login');
  };

  return (
    <div className="page-container admin-page">
      <div className="admin-topbar">
        <h1 className="page-title">Resources</h1>
        <div className="admin-topbar-actions">
          <Link to="/admin/analytics" className="pill-btn">Analytics</Link>
          <Link to="/admin/import-export" className="pill-btn" data-testid="import-export-link">
            <Upload size={12} /> Import / Export
          </Link>
          <Link to="/admin/resources/new" className="pill-btn primary" data-testid="admin-create-link">
            <Plus size={12} /> New resource
          </Link>
          <button type="button" onClick={onLogout} className="pill-btn ghost" data-testid="admin-logout">
            <LogOut size={12} />
          </button>
        </div>
      </div>
      <form onSubmit={onSearch} className="admin-search">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name..."
          data-testid="admin-search-input"
        />
        <select
          value={typeFilter}
          onChange={(e) => { setTypeFilter(e.target.value); load(q, e.target.value); }}
          data-testid="admin-type-filter"
        >
          <option value="">All types</option>
          {types.map((t) => (
            <option key={t.slug} value={t.slug}>{t.plural || t.name}</option>
          ))}
        </select>
        <button type="submit" className="pill-btn">Search</button>
      </form>
      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : (
        <table className="admin-table" data-testid="admin-resources-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Category</th>
              <th>Pricing</th>
              <th>Flags</th>
              <th>Clicks</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {tools.map((t) => (
              <tr key={t.id}>
                <td><Link to={`/tool/${t.slug}`}>{t.name}</Link></td>
                <td><span className="chip">{t.resource_type}</span></td>
                <td>{t.primary_category}{t.subcategory ? ` › ${t.subcategory}` : ''}</td>
                <td>{t.pricing_type}</td>
                <td>
                  {t.featured && <span className="badge featured">F</span>}{' '}
                  {t.sponsored && <span className="badge sponsored">S</span>}{' '}
                  {t.verified && <span className="badge chip-t">V</span>}{' '}
                  {t.badges?.length > 0 && <span className="badge chip-r">{t.badges.length}★</span>}
                </td>
                <td>{t.click_count}</td>
                <td>
                  <Link to={`/admin/tools/${t.slug}/edit`} className="pill-btn ghost sm">Edit</Link>
                </td>
              </tr>
            ))}
            {tools.length === 0 && (
              <tr><td colSpan={7}><div className="empty-state">No resources match.</div></td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
